
  import { defineComponent, ref, computed } from 'vue';
  import { useStore } from 'vuex';
  import { Actions } from '@/store/enums/StoreEnums';
  import { getCSSVariableValue } from '@/assets/ts/_utils';
  import { useI18n } from 'vue-i18n/index';
  import {
    pct,
    textColor,
    arrowSrc,
    arrow,
  } from '@/core/helpers/dashboard-utils';
  import CustomDatePicker from '@/components/search/CustomDatePicker.vue';

  export default defineComponent({
    name: 'subscriptions-report-widget',
    props: {
      widgetClasses: String,
    },
    components: {
      CustomDatePicker,
    },
    async setup() {
      const { t, te } = useI18n();
      const loading = ref(false);
      const translate = (text) => {
        if (te(text)) {
          return t(text);
        } else {
          return text;
        }
      };
      const selectedPeriod = ref('last-7-days');
      const customDateFrom = ref('');
      const customDateTo = ref('');
      const showDatePicker = ref(false);
      interface SubscriptionData {
        packageType: string;
        title: string;
        count: number;
        revenues: number;
        revenuesChangePercentage: number;
      }
      const arrayOfData = ref<SubscriptionData[]>([]);
      const total = ref<SubscriptionData>();

      const store = useStore();
      await store.dispatch(Actions.GET_MAP_PACKAGES_DASHBOARD);
      const data = computed(() => store.getters.mapPackagesDashboard);

      if (Array.isArray(data.value.data))
        arrayOfData.value = data.value.data.slice(0, -1);
      total.value = data.value.data.slice(-1)[0];

      const showCustomDatePicker = () => {
        showDatePicker.value = true;
        selectedPeriod.value = 'custom';
      };

      const applyCustomDates = async () => {
        if (!customDateFrom.value || !customDateTo.value) {
          alert('Please select both dates');
          return;
        }
        showDatePicker.value = false;
        await selectPeriod('custom');
      };

      const selectPeriod = async (period: string) => {
        loading.value = true;
        selectedPeriod.value = period;
        const obj = {
          dateRange: period,
          showTrend: false,
          dateFrom: '',
          dateTo: '',
        };

        if (period === 'custom' && customDateFrom.value && customDateTo.value) {
          obj.dateFrom = customDateFrom.value;
          obj.dateTo = customDateTo.value;
        }

        await store.dispatch(Actions.GET_MAP_PACKAGES_DASHBOARD, obj);
        const payload = store.getters.mapPackagesDashboard;
        if (Array.isArray(payload?.data))
          arrayOfData.value = payload.data.slice(0, -1);
        total.value = payload?.data?.slice(-1)[0];
        loading.value = false;
      };

      const primaryColor = getCSSVariableValue('--bs-primary');
      const warningColor = getCSSVariableValue('--bs-warning');
      const infoColor = getCSSVariableValue('--bs-info');

      const getClassBg = (packageType) => {
        switch (packageType) {
          case 'enterprise-pack':
            return 'bg-primary';
          case 'pro-pack':
            return 'bg-warning';
          case 'starter-pack':
            return 'bg-info';
          default:
            return 'bg-secondary';
        }
      };

      const hasData = computed(() => {
        return (
          arrayOfData.value &&
          arrayOfData.value.length > 0 &&
          arrayOfData.value.some((item) => item.count > 0)
        );
      });

      const chartSeries = computed(() => {
        if (!hasData.value) {
          return [1];
        }
        return arrayOfData.value.map((element) => element.count);
      });

      const chartOptions = computed(() => ({
        chart: {
          fontFamily: 'inherit',
          type: 'donut',
          toolbar: {
            show: false,
          },
        },
        plotOptions: {
          pie: {
            donut: {
              size: '70%',
              labels: {
                show: false,
              },
            },
          },
        },
        legend: {
          show: false,
        },
        dataLabels: {
          enabled: false,
        },
        stroke: {
          width: 2,
        },
        colors: hasData.value
          ? [primaryColor, warningColor, infoColor]
          : ['#E4E6EF'],
        labels: hasData.value
          ? arrayOfData.value.map((item) => item.title)
          : ['No Data'],
        tooltip: {
          enabled: hasData.value,
          style: {
            fontSize: '12px',
          },
          y: {
            formatter: function (val: number) {
              return val + ' subscriptions';
            },
          },
        },
        responsive: [
          {
            breakpoint: 480,
            options: {
              chart: {
                width: 180,
              },
            },
          },
        ],
      }));

      return {
        getClassBg,
        total,
        arrayOfData,
        data,
        loading,
        chartOptions,
        chartSeries,
        selectedPeriod,
        selectPeriod,
        textColor,
        arrowSrc,
        arrow,
        pct,
        translate,
        showDatePicker,
        customDateFrom,
        customDateTo,
        showCustomDatePicker,
        applyCustomDates,
      };
    },
  });
